<script>
import { useLayouts } from '@/@layouts';
import { VerticalNav } from '@/@layouts/components';
import HeaderMain from '@/components/header/HeaderMain';
import NotificationBar from '@/components/notifications/NotificationBar.vue';

export default defineComponent({
  props: {
    navItems: {
      type: Array,
      required: true,
    },
    verticalNavAttrs: {
      type: Object,
      default: () => ({}),
    },
  },
  setup(props, { slots }) {
    const { y: windowScrollY } = useWindowScroll();
    const { width: windowWidth } = useWindowSize();
    const {
      _layoutClasses: layoutClasses,
      isLessThanOverlayNavBreakpoint,
      isVerticalNavCollapsed,
      appContentLayoutNav,
      isNavbarBlurEnabled,
    } = useLayouts();
    const isOverlayNavActive = ref(false);
    const isLayoutOverlayVisible = ref(false);
    const toggleIsOverlayNavActive = useToggle(isOverlayNavActive);

    // ℹ️ This is alternative to below two commented watcher
    // We want to show overlay if overlay nav is visible and want to hide overlay if overlay is hidden and vice versa.
    syncRef(isOverlayNavActive, isLayoutOverlayVisible);

    // watch(isOverlayNavActive, value => {
    //   // Sync layout overlay with overlay nav
    //   isLayoutOverlayVisible.value = value
    // })
    // watch(isLayoutOverlayVisible, value => {
    //   // If overlay is closed via click, close hide overlay nav
    //   if (!value) isOverlayNavActive.value = false
    // })
    // ℹ️ Hide overlay if user open overlay nav in <md and increase the window width without closing overlay nav
    watch(windowWidth, (value) => {
      if (!isLessThanOverlayNavBreakpoint.value(value) && isLayoutOverlayVisible.value)
        isLayoutOverlayVisible.value = false;
    });

    const router = useRouter();
    const shallShowPageLoading = ref(false);

    return () => {
      const verticalNavAttrs = toRef(props, 'verticalNavAttrs');
      const {
        wrapper: verticalNavWrapper,
        wrapperProps: verticalNavWrapperProps,
        ...additionalVerticalNavAttrs
      } = verticalNavAttrs.value;

      // 👉 Vertical nav
      const verticalNav = h(
        VerticalNav,
        {
          class: {
            collapsed:
              isVerticalNavCollapsed.value &&
              appContentLayoutNav.value === 'vertical' &&
              !isLessThanOverlayNavBreakpoint.value(windowWidth),
            uncollapsed:
              !isVerticalNavCollapsed.value &&
              appContentLayoutNav.value === 'vertical' &&
              !isLessThanOverlayNavBreakpoint.value(windowWidth),
          },
          isOverlayNavActive: isOverlayNavActive.value,
          toggleIsOverlayNavActive,
          navItems: props.navItems,
          ...additionalVerticalNavAttrs,
        },
        {
          'nav-header': () => slots['vertical-nav-header']?.(),
          'before-nav-items': () => slots['before-vertical-nav-items']?.(),
          'after-nav-items': () => slots['after-vertical-nav-items']?.(),
        },
      );

      // 👉 Notification bar
      const notificationBar = h(NotificationBar);

      // 👉 Header
      const header = h(HeaderMain, {
        class: ['layout-default-header'],
      });

      // 👉 Content area
      let mainChildren = slots.default?.();

      // 💡 Only show loading and attach `beforeEach` & `afterEach` hooks if `content-loading` slot is used
      if (slots['content-loading']) {
        router.beforeEach(() => {
          console.info('setting to true');
          shallShowPageLoading.value = true;
        });
        router.afterEach(() => {
          console.info('setting to false');
          shallShowPageLoading.value = false;
        });
        mainChildren = shallShowPageLoading.value
          ? slots['content-loading']?.()
          : slots.default?.();
      }
      const main = h(
        'main',
        { class: 'layout-page-content' },
        h('div', { class: 'page-content-container' }, mainChildren),
      );

      // 👉 Footer
      const footer = h('footer', { class: 'layout-footer' }, [
        h('div', {}, slots.footer?.()),
      ]);

      // 👉 Overlay
      const layoutOverlay = h('div', {
        class: ['layout-overlay', { visible: isLayoutOverlayVisible.value }],
        onClick: () => {
          isLayoutOverlayVisible.value = !isLayoutOverlayVisible.value;
        },
      });

      return h(
        'div',
        {
          class: ['layout-wrapper', ...layoutClasses.value(windowWidth.value, windowScrollY.value)],
        },
        [
          notificationBar,
          header,
          verticalNavWrapper
            ? h(verticalNavWrapper, verticalNavWrapperProps, { default: () => verticalNav })
            : verticalNav,
          h('div', { class: 'layout-content-wrapper' }, [main, footer]),
          layoutOverlay,
        ],
      );
    };
  },
});
</script>

<style lang="scss">
@use '@configured-variables' as variables;
@use '@/@layouts/styles/placeholders';
@use '@/@layouts/styles/mixins';

.layout-wrapper.layout-nav-type-vertical {
  // TODO(v2): Check why we need height in vertical nav & min-height in horizontal nav
  // block-size: 100vh;
  display: flex;
  flex-direction: column;
  padding-top: var(--notification-bar-height, 0px);

  .layout-content-wrapper {
    display: flex;
    flex-direction: column;
    flex-grow: 1;
    min-block-size: 0;
    transition: padding-inline-start 0.2s ease-in-out;
    will-change: padding-inline-start;
  }

  .layout-page-content {
    flex-grow: 1;
  }

  .layout-footer {
    margin-top: auto;
  }

  .layout-navbar {
    z-index: variables.$layout-vertical-nav-layout-navbar-z-index;

    .navbar-content-container {
      block-size: variables.$layout-vertical-nav-navbar-height;
    }

    @at-root {
      .layout-wrapper.layout-nav-type-vertical {
        .layout-navbar {
          @if variables.$layout-vertical-nav-navbar-is-contained {
            @include mixins.boxed-content;
          } @else {
            .navbar-content-container {
              @include mixins.boxed-content;
            }
          }
        }
      }
    }
  }

  &.layout-navbar-sticky .layout-navbar {
    @extend %layout-navbar-sticky;
    inset-block-start: var(--notification-bar-height, 0px);
  }

  &.layout-settings-page .layout-default-header {
    position: sticky;
    inset-block-start: var(--notification-bar-height, 0px);
    z-index: variables.$layout-vertical-nav-layout-navbar-z-index;
  }

  &.layout-navbar-hidden .layout-navbar {
    @extend %layout-navbar-hidden;
  }

  // 👉 Footer
  .layout-footer {
    @include mixins.boxed-content;
  }

  // 👉 Layout overlay
  .layout-overlay {
    position: fixed;
    z-index: variables.$layout-overlay-z-index;
    background-color: rgb(0 0 0 / 60%);
    cursor: pointer;
    inset: 0;
    opacity: 0;
    pointer-events: none;
    transition: opacity 0.25s ease-in-out;
    will-change: transform;

    &.visible {
      opacity: 1;
      pointer-events: auto;
    }
  }

  &:not(.layout-overlay-nav) .layout-content-wrapper {
    padding-inline-start: variables.$layout-vertical-nav-width;
  }

  // Adjust right column pl when vertical nav is collapsed
  &.layout-vertical-nav-collapsed .layout-content-wrapper {
    padding-inline-start: variables.$layout-vertical-nav-collapsed-width;
  }

  // 👉 Header logo slot — keep it the same width as the vertical nav so the
  // breadcrumb that follows always starts at the nav's right edge instead of
  // sliding under the pinned panel. The slot width animates in sync with the
  // nav (same 0.25s ease-in-out as .layout-vertical-nav inline-size).
  // 32px = the header's own inline padding (.header padding-inline on laptop+).
  &:not(.layout-overlay-nav) .layout-default-header .navigation-link {
    flex: 0 0 auto;
    inline-size: calc(#{variables.$layout-vertical-nav-width} - 32px);
    transition: inline-size 0.25s ease-in-out;
    will-change: inline-size;
  }

  &:not(.layout-overlay-nav).layout-vertical-nav-collapsed .layout-default-header .navigation-link {
    inline-size: calc(#{variables.$layout-vertical-nav-collapsed-width} - 32px);
  }

  // 👉 Content height fixed
  &.layout-content-height-fixed {
    .layout-content-wrapper {
      max-block-size: calc(var(--vh) * 100);
    }

    .layout-page-content {
      display: flex;
      overflow: hidden;

      .page-content-container {
        inline-size: 100%;

        > :first-child {
          max-block-size: 100%;
          overflow-y: auto;
        }
      }
    }
  }
}
</style>
